/**
 * Adds the upsell variant together with the main product when the checkbox is checked.
 */
class BuyItWith extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    const productForm = this.closest('product-form');
    if (!productForm) return;

    const form = productForm.querySelector('form');
    if (!form) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    productForm.addEventListener('submit', (event) => this.#onSubmit(event, productForm, form), {
      capture: true,
      signal,
    });

    const checkbox = this.querySelector('[data-buy-it-with-checkbox]');
    const card = this.querySelector('[data-buy-it-with-card]');

    const updateSelectedState = () => {
      card?.classList.toggle('is-selected', Boolean(checkbox?.checked));
    };

    checkbox?.addEventListener('change', updateSelectedState, { signal });
    updateSelectedState();

    const variantSelect = this.querySelector('[data-buy-it-with-variant]');
    const priceEl = this.querySelector('[data-buy-it-with-price]');

    if (variantSelect && priceEl) {
      variantSelect.addEventListener(
        'change',
        () => {
          const option = variantSelect.selectedOptions[0];
          if (option?.dataset.priceFormatted) {
            priceEl.textContent = option.dataset.priceFormatted;
          }
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  async #onSubmit(event, productForm, form) {
    const checkbox = this.querySelector('[data-buy-it-with-checkbox]');
    if (!checkbox?.checked) return;

    const variantSelect = this.querySelector('[data-buy-it-with-variant]');
    const upsellVariantId = variantSelect?.value;
    if (!upsellVariantId) return;

    const mainVariantInput = form.querySelector('[name="id"]');
    const mainVariantId = mainVariantInput?.value;
    if (!mainVariantId) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (event.submitter?.getAttribute('aria-busy') === 'true') return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const submitButtons = Array.from(form.elements).filter((button) => button.type === 'submit');
    submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const quantityInput = form.querySelector('[name="quantity"]');
    const quantity = Math.max(1, parseInt(quantityInput?.value || '1', 10) || 1);

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [
            { id: parseInt(mainVariantId, 10), quantity },
            { id: parseInt(upsellVariantId, 10), quantity: 1 },
          ],
          sections: sectionsToBundle.join(','),
          sections_url: `${Shopify.routes.root}variants/${mainVariantId}`,
        }),
      });

      submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      const responseJson = await response.json();

      if (response.ok) {
        if (
          window.themeVariables.settings.cartType === 'page' ||
          window.themeVariables.settings.pageType === 'cart'
        ) {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        form.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: responseJson.items ?? [responseJson],
              cart: cartContent,
              onSuccessDo: null,
            },
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              onSuccessDo: null,
              cart: cartContent,
            },
          })
        );
      } else {
        form.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              error: responseJson.description,
            },
          })
        );
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch {
      submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }
}

if (!window.customElements.get('buy-it-with')) {
  window.customElements.define('buy-it-with', BuyItWith);
}
